import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { translations, type Lang } from "../content";

type Prefs = { fontSize: number; highContrast: boolean; hidePhotos: boolean };

export default function AccessibilityPanel({
  onClose,
  lang,
}: {
  onClose: () => void;
  lang: Lang;
}) {
  const t = translations[lang].a11y;
  const [fontSize, setFontSize] = useState(100);
  const [highContrast, setHighContrast] = useState(false);
  const [hidePhotos, setHidePhotos] = useState(false);

  const apply = (p: Prefs) => {
    const root = document.documentElement;
    root.style.fontSize = `${(p.fontSize / 100) * 16}px`;
    root.classList.toggle("high-contrast", p.highContrast);
    root.classList.toggle("hide-photos", p.hidePhotos);
    localStorage.setItem("accessibility", JSON.stringify(p));
  };

  useEffect(() => {
    const saved = localStorage.getItem("accessibility");
    if (!saved) return;
    try {
      const p = JSON.parse(saved) as Prefs;
      setFontSize(p.fontSize ?? 100);
      setHighContrast(!!p.highContrast);
      setHidePhotos(!!p.hidePhotos);
      apply({ fontSize: p.fontSize ?? 100, highContrast: !!p.highContrast, hidePhotos: !!p.hidePhotos });
    } catch {
      /* ignore malformed prefs */
    }
  }, []);

  const update = (patch: Partial<Prefs>) => {
    const next = { fontSize, highContrast, hidePhotos, ...patch };
    setFontSize(next.fontSize);
    setHighContrast(next.highContrast);
    setHidePhotos(next.hidePhotos);
    apply(next);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      dir={lang === "he" ? "rtl" : "ltr"}
      className="fixed bottom-28 z-50 w-72 panel p-6 text-foreground ltr:right-6 rtl:left-6"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-primary">{t.title}</h3>
        <button onClick={onClose} aria-label="Close" className="hover:text-primary transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      <label className="block text-sm mb-2">
        {t.fontSize}: {fontSize}%
      </label>
      <input
        type="range"
        min={80}
        max={150}
        step={10}
        value={fontSize}
        onChange={(e) => update({ fontSize: Number(e.target.value) })}
        className="w-full mb-4 accent-[#D4AF37]"
      />

      <label className="flex items-center gap-2 text-sm mb-2 cursor-pointer">
        <input
          type="checkbox"
          checked={highContrast}
          onChange={(e) => update({ highContrast: e.target.checked })}
        />
        {t.contrast}
      </label>

      <label className="flex items-center gap-2 text-sm mb-4 cursor-pointer">
        <input
          type="checkbox"
          checked={hidePhotos}
          onChange={(e) => update({ hidePhotos: e.target.checked })}
        />
        {t.hidePhotos}
      </label>

      <button
        onClick={() => update({ fontSize: 100, highContrast: false, hidePhotos: false })}
        className="text-xs text-primary underline"
      >
        {t.reset}
      </button>
    </motion.div>
  );
}
